import type { DotLottieWorker as DotLottieWorkerType } from "@lottiefiles/dotlottie-web"
import { loadPlayer } from "./dotlottie_player"

// Preload all lottie animation asset urls
const animations = import.meta.glob(
  [
    "images/home/prosumer/diy*.lottie",
    "images/home/prosumer/smarthome*.lottie",
    "images/home/prosumer/productivity*.lottie",
  ],
  {
    query: "?url",
    eager: true,
  },
)

export class HomepageModuleAnimation {
  dotLottie: DotLottieWorkerType | null = null
  observer: IntersectionObserver | null = null
  container: HTMLElement
  loaded: boolean = false
  visible: boolean = false
  phone = window.matchMedia("(max-width: 723px)")

  constructor(container: HTMLElement) {
    this.container = container
    this.setupIntersectionObserver()
    this.setupPlaybackObserver()

    // Each breakpoint has its own cut of the artwork, so crossing it swaps the file; a
    // plain width change only re-syncs the canvas bitmap with its CSS size.
    this.phone.addEventListener("change", () => {
      if (this.loaded) this.reload()
    })
    new ResizeObserver(() => {
      this.dotLottie?.resize()
    }).observe(this.container)
  }

  async reload() {
    await this.dotLottie?.destroy()
    this.dotLottie = null
    this.container.querySelector("canvas")?.remove()
    this.container.classList.remove("is-loaded", "has-failed")
    this.container.style.backgroundImage = ""
    await this.loadAnimation()
  }

  // A failure leaves the artwork's opening frame up for good, and that is its least
  // informative one — an empty card, an unfilled Applet. Swap in the composed frame, the same
  // one a reader who gets no animation sees. Painting the URL here rather than in the markup
  // is what keeps a working page from fetching it.
  showFallbackFrame() {
    const frame = this.phone.matches ? this.container.dataset.fallbackMobile : this.container.dataset.fallbackDesktop

    if (!frame) return

    this.container.style.backgroundImage = `url("${frame}")`
    this.container.classList.add("has-failed")
  }

  setupIntersectionObserver() {
    // The poster covers the parse, so this only buys lead for the .lottie fetch — measured
    // cold, the static frame shows for under 300ms. Wider mostly parses modules nobody reaches.
    const options = {
      root: null,
      rootMargin: "100px 0px",
      threshold: 0,
    }

    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.loaded) {
          this.loadAnimation()
          this.loaded = true
          this.observer?.disconnect()
        }
      })
    }, options)

    this.observer.observe(this.container)
  }

  // Playback follows visibility rather than loading, so a module parsed ahead of the reader
  // is still on its opening frame when they arrive, and picks up where it left off if they
  // scroll away and come back.
  setupPlaybackObserver() {
    // A fifth of the box, so the loop does not start while the module is a sliver at the
    // bottom edge — the reader would then arrive to the middle of it.
    const options = { threshold: 0.2 }

    new IntersectionObserver(entries => {
      entries.forEach(entry => {
        this.visible = entry.isIntersecting

        if (entry.isIntersecting) {
          this.dotLottie?.play()
        } else {
          this.dotLottie?.pause()
        }
      })
    }, options).observe(this.container)
  }

  async loadAnimation() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return
    }

    // Phones get the 393×180 cut of each artwork; everything else the 1344×480 one.
    const animationPath = this.phone.matches
      ? this.container.dataset.animationMobile
      : this.container.dataset.animationDesktop

    try {
      const animationUrl = (animations[animationPath as string] as { default: string })?.default

      if (!animationUrl) {
        console.error("Animation not found:", animationPath)
        return
      }

      const { DotLottieWorker } = await loadPlayer()

      const canvas = document.createElement("canvas")
      this.container.appendChild(canvas)

      this.dotLottie = new DotLottieWorker({
        canvas,
        src: animationUrl,
        workerId: "dotlottie-shared",
        loop: true,
        autoplay: false,
      })

      this.dotLottie.addEventListener("load", () => {
        if (this.visible) this.dotLottie?.play()
      })

      // The asset is fetched inside the worker, so a failure there arrives as an event rather
      // than rejecting the call above.
      this.dotLottie.addEventListener("loadError", () => this.showFallbackFrame())

      // The canvas draws the same frame the poster shows, so the poster can go the moment
      // there is anything on it.
      const dropPoster = () => {
        this.container.classList.add("is-loaded")
        this.dotLottie?.removeEventListener("render", dropPoster)
      }
      this.dotLottie.addEventListener("render", dropPoster)
    } catch (error) {
      console.error("Failed to load dotLottie animation:", error)
      this.showFallbackFrame()
    }
  }
}

const initHomepageModuleAnimations = () => {
  document.querySelectorAll(".homepage-module__animation-container").forEach(container => {
    new HomepageModuleAnimation(container as HTMLElement)
  })
}

// Waiting for `load` would hold the observers back until the hero collage and the fonts are
// done, which is most of the time a reader spends before the first scroll.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHomepageModuleAnimations, { once: true })
} else {
  initHomepageModuleAnimations()
}
